/**
 * Line item targeting — the `targeting` object of a line item, in exactly the
 * shape the API reads and writes (see LineItem_input.json in core/api).
 *
 * Every target either narrows delivery to what it names or, when `negated`,
 * excludes it. Targets of different types are AND'ed together; the entries
 * within one type are OR'ed.
 */

import type { LucideIcon } from 'lucide-react'
import {
  CalendarRange,
  Clock,
  Eye,
  Flag,
  Handshake,
  Keyboard,
  Layers,
  Link,
  MapPin,
  MessageSquareText,
  Network,
  Newspaper,
  Rss,
  Tablet,
  Tag,
  Target,
  Umbrella,
  Users,
  Wifi,
} from 'lucide-react'

// ─── Building blocks ────────────────────────────────────────────────────────

/** A reference to another object. The API accepts a bare id string too. */
export interface ObjectRef {
  id: string
  name?: string
  description?: string
}

/** A location from the location search — ids are numeric, unlike object refs. */
export interface NamedLocationRef {
  id: number
  name?: string
  type?: string
  context?: string
  active?: boolean
  ipReach?: string
}

export interface MunicipalityRef {
  id: number
  name?: string
}

/**
 * Key-value entries: key → the values that match it, where an empty value list
 * matches any value. A key prefixed with `NOT ` inverts that row.
 */
export type KeyValueEntries = Record<string, string[] | string[][]>

export interface KeyValueTarget {
  entries?: KeyValueEntries
  notEntries?: KeyValueEntries
}

/**
 * One user-segment target, a group of segments. How the ad server combines
 * the groups is set out under "Segment rows" below.
 */
export interface UserSegmentTarget {
  userSegments?: ObjectRef[]
  notUserSegments?: ObjectRef[]
}

export interface DateRange {
  /** Local date-time, `YYYY-MM-DDTHH:mm`. */
  first: string
  second: string
}

export type TimeZoneSetting = 'SYSTEM' | 'USER'

/**
 * A sentence the embedding service has encoded. The server keeps the vector
 * and only accepts a sentence whose id matches the one it stored for that
 * text, so a target can only be built from what the embedding endpoint
 * returned.
 */
export interface SemanticSentence {
  id: number
  sentence: string
}

/**
 * Matches articles whose meaning is close enough to a sentence. `score` is the
 * similarity threshold, one of {@link SEMANTIC_MATCH_LEVELS}.
 */
export interface SemanticTarget {
  sentence: SemanticSentence
  score: number
  negated?: boolean
  /** True to skip articles the sentiment analysis reads as negative. */
  positiveSentimentOnly?: boolean
  matchingGroup?: number
}

export const DAYS_OF_WEEK = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const
export type DayOfWeek = (typeof DAYS_OF_WEEK)[number]

// ─── The targeting object ───────────────────────────────────────────────────

export interface Targeting {
  publisherTarget?: { earningsAccounts?: ObjectRef[]; negated?: boolean }
  siteTarget?: { sites?: ObjectRef[]; negated?: boolean }
  siteGroupTarget?: { siteGroups?: ObjectRef[]; negated?: boolean }
  adUnitTarget?: { adUnits?: ObjectRef[]; negated?: boolean }
  adUnitMatchingLabelTargets?: { matchingLabels?: string[] }[]
  siteCountryTarget?: { countries?: string[] }
  userSegmentTargets?: UserSegmentTarget[]
  keywordTargets?: { keywords?: string[]; notKeywords?: string[] }[]
  categoryTargets?: { categories?: string[]; notCategories?: string[] }[]
  keyValueTargets?: KeyValueTarget[]
  deviceTargets?: {
    targetedDeviceTypes?: string[]
    targetedOSes?: string[]
    targetedBrands?: string[]
  }[]
  dayPartingTargets?: {
    daysOfWeek?: string[]
    hoursOfDay?: number[]
    timeZoneSetting?: TimeZoneSetting
  }[]
  dateTarget?: { dateRanges?: DateRange[]; timeZoneSetting?: TimeZoneSetting }
  namedLocationTarget?: {
    locations?: NamedLocationRef[]
    municipalities?: MunicipalityRef[]
    negated?: boolean
  }
  domainNameTarget?: { names?: string[]; negated?: boolean }
  articleTarget?: { urls?: string[]; negated?: boolean }
  ipAddressTarget?: { addresses?: string[] }
  viewabilityTarget?: { viewability?: number }

  semanticTargets?: SemanticTarget[]

  /**
   * Types this editor does not edit — geospatial areas need a map,
   * similar-keyword targeting needs the embedding platform, retargeting is
   * handled outside this UI, and the audience, weather and organisation targets
   * need their own network setup. They are carried through a save untouched so
   * editing here never drops them.
   */
  geospatialTargets?: unknown[]
  retargetingTargets?: KeyValueTarget[]
  similarKeywordTargets?: unknown[]
  weatherTargets?: unknown[]
  thirdPartyAudienceTargets?: unknown[]
  firstPartyAudienceTarget?: { firstPartyAudiences?: unknown[] }
  organisationTarget?: { organisations?: string[]; negated?: boolean }
}

/** The fields this editor owns; everything else on a targeting is passed through. */
export const EDITED_FIELDS = [
  'publisherTarget',
  'siteTarget',
  'siteGroupTarget',
  'adUnitTarget',
  'adUnitMatchingLabelTargets',
  'siteCountryTarget',
  'userSegmentTargets',
  'keywordTargets',
  'categoryTargets',
  'keyValueTargets',
  'deviceTargets',
  'dayPartingTargets',
  'dateTarget',
  'namedLocationTarget',
  'domainNameTarget',
  'articleTarget',
  'ipAddressTarget',
  'viewabilityTarget',
  'semanticTargets',
] as const

// ─── Type registry ──────────────────────────────────────────────────────────

export type TargetingTypeId =
  | 'publisher'
  | 'site'
  | 'siteGroup'
  | 'adUnit'
  | 'adUnitMatchingLabel'
  | 'siteCountry'
  | 'userSegment'
  | 'keyword'
  | 'category'
  | 'semantic'
  | 'keyValue'
  | 'device'
  | 'dayParting'
  | 'date'
  | 'namedLocation'
  | 'domain'
  | 'url'
  | 'ipAddress'
  | 'viewability'

export interface TargetingTypeDef {
  id: TargetingTypeId
  /** The backend's own `TargetType` name — what a product's limits are stated in. */
  apiType: string
  /** The `targeting` field this type reads and writes. */
  field: keyof Targeting
  icon: LucideIcon
  /** Which of the editor's groups the type is listed under. */
  group: 'inventory' | 'audience' | 'context' | 'time'
  /** How many things the type currently targets. */
  count: (t: Targeting) => number
  /** True when the selection excludes rather than includes. */
  negated?: (t: Targeting) => boolean
}

const len = (a?: unknown[]) => a?.length ?? 0

export const TARGETING_TYPES: TargetingTypeDef[] = [
  {
    id: 'publisher',
    apiType: 'PUBLISHER',
    field: 'publisherTarget',
    icon: Handshake,
    group: 'inventory',
    count: (t) => len(t.publisherTarget?.earningsAccounts),
    negated: (t) => !!t.publisherTarget?.negated,
  },
  {
    id: 'site',
    apiType: 'SITE',
    field: 'siteTarget',
    icon: Network,
    group: 'inventory',
    count: (t) => len(t.siteTarget?.sites),
    negated: (t) => !!t.siteTarget?.negated,
  },
  {
    id: 'siteGroup',
    apiType: 'SITE_GROUP',
    field: 'siteGroupTarget',
    icon: Umbrella,
    group: 'inventory',
    count: (t) => len(t.siteGroupTarget?.siteGroups),
    negated: (t) => !!t.siteGroupTarget?.negated,
  },
  {
    id: 'adUnit',
    apiType: 'AD_UNIT',
    field: 'adUnitTarget',
    icon: Newspaper,
    group: 'inventory',
    count: (t) => len(t.adUnitTarget?.adUnits),
    negated: (t) => !!t.adUnitTarget?.negated,
  },
  {
    id: 'adUnitMatchingLabel',
    apiType: 'AD_UNIT_MATCHING_LABEL',
    field: 'adUnitMatchingLabelTargets',
    icon: Tag,
    group: 'inventory',
    // One label per target, so every row counts.
    count: (t) =>
      (t.adUnitMatchingLabelTargets ?? []).reduce((n, row) => n + len(row.matchingLabels), 0),
  },
  {
    id: 'siteCountry',
    apiType: 'SITE_COUNTRY',
    field: 'siteCountryTarget',
    icon: Flag,
    group: 'inventory',
    count: (t) => len(t.siteCountryTarget?.countries),
  },
  {
    id: 'userSegment',
    apiType: 'USER_SEGMENT',
    field: 'userSegmentTargets',
    icon: Users,
    group: 'audience',
    count: (t) =>
      (t.userSegmentTargets ?? []).reduce(
        (n, row) => n + len(row.userSegments) + len(row.notUserSegments),
        0
      ),
  },
  {
    id: 'namedLocation',
    apiType: 'NAMED_LOCATION',
    field: 'namedLocationTarget',
    icon: MapPin,
    group: 'audience',
    count: (t) =>
      len(t.namedLocationTarget?.locations) + len(t.namedLocationTarget?.municipalities),
    negated: (t) => !!t.namedLocationTarget?.negated,
  },
  {
    id: 'device',
    apiType: 'DEVICE_PROPERTY',
    field: 'deviceTargets',
    icon: Tablet,
    group: 'audience',
    count: (t) =>
      (t.deviceTargets ?? []).reduce(
        (n, row) =>
          n + len(row.targetedDeviceTypes) + len(row.targetedOSes) + len(row.targetedBrands),
        0
      ),
  },
  {
    id: 'keyword',
    apiType: 'KEYWORD',
    field: 'keywordTargets',
    icon: Target,
    group: 'context',
    count: (t) =>
      (t.keywordTargets ?? []).reduce(
        (n, row) => n + countPiped(row.keywords) + len(row.notKeywords),
        0
      ),
  },
  {
    id: 'category',
    apiType: 'CATEGORY',
    field: 'categoryTargets',
    icon: Layers,
    group: 'context',
    count: (t) =>
      (t.categoryTargets ?? []).reduce(
        (n, row) => n + countPiped(row.categories) + len(row.notCategories),
        0
      ),
  },
  {
    id: 'semantic',
    apiType: 'SEMANTIC_CATEGORY',
    field: 'semanticTargets',
    icon: MessageSquareText,
    group: 'context',
    count: (t) => len(t.semanticTargets),
  },
  {
    id: 'keyValue',
    apiType: 'KEY_VALUE',
    field: 'keyValueTargets',
    icon: Keyboard,
    group: 'context',
    count: (t) => countKeyValue(t.keyValueTargets),
  },
  {
    id: 'url',
    apiType: 'ARTICLE',
    field: 'articleTarget',
    icon: Link,
    group: 'context',
    count: (t) => len(t.articleTarget?.urls),
    negated: (t) => !!t.articleTarget?.negated,
  },
  {
    id: 'domain',
    apiType: 'DOMAIN_NAME',
    field: 'domainNameTarget',
    icon: Rss,
    group: 'context',
    count: (t) => len(t.domainNameTarget?.names),
    negated: (t) => !!t.domainNameTarget?.negated,
  },
  {
    id: 'ipAddress',
    apiType: 'IP_ADDRESS',
    field: 'ipAddressTarget',
    icon: Wifi,
    group: 'context',
    count: (t) => len(t.ipAddressTarget?.addresses),
  },
  {
    id: 'viewability',
    apiType: 'AD_UNIT_VIEWABILITY',
    field: 'viewabilityTarget',
    icon: Eye,
    group: 'context',
    count: (t) => (typeof t.viewabilityTarget?.viewability === 'number' ? 1 : 0),
  },
  {
    id: 'dayParting',
    apiType: 'DAY_PARTING',
    field: 'dayPartingTargets',
    icon: Clock,
    group: 'time',
    count: (t) => len(t.dayPartingTargets),
  },
  {
    id: 'date',
    apiType: 'DATE',
    field: 'dateTarget',
    icon: CalendarRange,
    group: 'time',
    count: (t) => len(t.dateTarget?.dateRanges),
  },
]

export const TARGETING_GROUPS = ['inventory', 'audience', 'context', 'time'] as const

/**
 * The targeting types a network has no use for. Publishers are only a concept
 * on a marketplace platform, where the inventory belongs to other networks;
 * site groups are the other way round — the network's own groupings of its own
 * sites, which a marketplace does not have.
 */
export function hiddenTargetingTypes(
  network?: { marketplacePlatform?: boolean; capabilities?: string[] } | null
): TargetingTypeId[] {
  const hidden: TargetingTypeId[] = network?.marketplacePlatform ? ['siteGroup'] : ['publisher']
  // Semantic targeting needs the network's embedding capability — or a
  // marketplace, where the server encodes sentences regardless
  // (TargetingQueryResource.getEmbedding). Elsewhere it refuses to, so there
  // is nothing to offer.
  if (!network?.marketplacePlatform && !network?.capabilities?.includes('SEMANTIC_TARGETING')) {
    hidden.push('semantic')
  }
  return hidden
}

/** Icon for a type id — used by summaries that only carry the id. */
export function targetingType(id: TargetingTypeId): TargetingTypeDef {
  const def = TARGETING_TYPES.find((d) => d.id === id)
  if (!def) throw new Error(`Unknown targeting type: ${id}`)
  return def
}

// ─── Semantic match levels ──────────────────────────────────────────────────
//
// The similarity thresholds the old UI offers, from loosest to strictest. A
// stored score is read as the strictest level it reaches, so a threshold set
// through the API that sits between two levels still has a name.

export const SEMANTIC_MATCH_LEVELS = [0.3, 0.4, 0.5, 0.6, 0.7] as const

export const DEFAULT_SEMANTIC_SCORE = 0.5

/** The 1-based match level a score reaches, or 0 below the loosest one. */
export function semanticMatchLevel(score: number | undefined): number {
  if (score === undefined) return 0
  let level = 0
  SEMANTIC_MATCH_LEVELS.forEach((threshold, i) => {
    if (score >= threshold - 1e-6) level = i + 1
  })
  return level
}

/**
 * The languages to search articles in, from the site countries targeted — the
 * embedding matches across languages, so without this a Norwegian campaign
 * would be shown Swedish articles too. Empty searches every language.
 */
const COUNTRY_LANGUAGES: Record<string, string[]> = {
  DK: ['da', 'dan'],
  NO: ['nb', 'no', 'nn', 'nor', 'nno', 'nob'],
  SE: ['sv', 'swe'],
  FI: ['fi', 'fin'],
}

export function semanticSearchLanguages(targeting: Targeting): string[] {
  return (targeting.siteCountryTarget?.countries ?? []).flatMap(
    (country) => COUNTRY_LANGUAGES[country] ?? []
  )
}

// ─── Keyword / category piping ──────────────────────────────────────────────
//
// A keyword or category target holds one flat list where the entries are AND'ed:
// `"a|b|c"` matches any of a, b or c, a bare `"x"` must match, and `"NOT x"`
// must not. Three inputs (any / all / none) are enough to express the common
// case, and that is what the list is parsed back into.

export interface PipedValues {
  /** Must match at least one of these. */
  any: string[]
  /** Must match every one of these. */
  all: string[]
  /** Must match none of these. */
  none: string[]
}

export const emptyPiped = (): PipedValues => ({ any: [], all: [], none: [] })

export function parsePiped(values?: string[]): PipedValues {
  const out = emptyPiped()
  const rows = values ?? []
  for (const raw of rows) {
    const value = raw.trim()
    if (!value) continue
    if (value.startsWith('NOT ')) {
      out.none.push(value.slice(4).trim())
    } else if (value.includes('|')) {
      out.any.push(
        ...value
          .split('|')
          .map((v) => v.trim())
          .filter(Boolean)
      )
    } else {
      out.all.push(value)
    }
  }
  // A lone value carries no pipe, so it cannot be told apart from one that
  // must match — and with nothing to AND it against the two mean the same
  // thing. It is read as "match any", which is where it was typed.
  if (rows.length === 1 && out.all.length === 1 && !out.any.length && !out.none.length) {
    return { ...out, any: out.all, all: [] }
  }
  return out
}

export function formatPiped(values: PipedValues): string[] {
  const out: string[] = []
  if (values.any.length) {
    // A lone alternative would carry no pipe, and a row without one reads as a
    // value that must match — so it would come back in the wrong list. The
    // trailing pipe keeps it an alternatives row: the ad server splits the row
    // on "|" and drops the empty tail (OrStringCriteria in core), so it still
    // matches exactly this one value.
    out.push(values.any.length === 1 ? `${values.any[0]}|` : values.any.join('|'))
  }
  out.push(...values.all)
  out.push(...values.none.map((v) => `NOT ${v}`))
  return out
}

function countPiped(values?: string[]): number {
  const parsed = parsePiped(values)
  return parsed.any.length + parsed.all.length + parsed.none.length
}

// ─── Key-value rows ─────────────────────────────────────────────────────────

/** One editable key-value row: `key` matches any of `values`, or none when negated. */
export interface KeyValueRow {
  key: string
  values: string
  /** Row-level negation, written back to the API as `NOT key`. */
  not: boolean
  /**
   * True for a row that lives in the group's `notEntries` block, which negates
   * the rows as a block rather than through a `NOT ` on each key. The two are
   * not the same thing, so a row read from there is written back there.
   */
  inNotBlock?: boolean
}

/** A group of rows that must all match. Groups are OR'ed with one another. */
export type KeyValueGroup = KeyValueRow[]

function readEntries(entries: KeyValueEntries | undefined, inNotBlock: boolean): KeyValueRow[] {
  const rows: KeyValueRow[] = []
  for (const [rawKey, rawValues] of Object.entries(entries ?? {})) {
    const negatedKey = rawKey.toUpperCase().startsWith('NOT ')
    const key = negatedKey ? rawKey.slice(4) : rawKey
    // A key's values are either one list, or a list of lists — one row each.
    const lists: string[][] = Array.isArray(rawValues[0])
      ? (rawValues as string[][])
      : [rawValues as string[]]
    for (const list of lists) {
      rows.push({
        key,
        values: (list ?? []).join(', '),
        not: negatedKey,
        ...(inNotBlock ? { inNotBlock: true } : {}),
      })
    }
  }
  return rows
}

export function parseKeyValueTargets(targets?: KeyValueTarget[]): KeyValueGroup[] {
  const groups = (targets ?? []).map((target) => [
    ...readEntries(target.entries, false),
    ...readEntries(target.notEntries, true),
  ])
  return groups.filter((group) => group.length > 0)
}

export function formatKeyValueTargets(groups: KeyValueGroup[]): KeyValueTarget[] {
  const targets: KeyValueTarget[] = []
  for (const group of groups) {
    const entries: Record<string, string[][]> = {}
    const notEntries: Record<string, string[][]> = {}
    for (const row of group) {
      const key = row.key.trim()
      if (!key) continue
      const values = row.values
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean)
      const into = row.inNotBlock ? notEntries : entries
      const apiKey = row.not ? `NOT ${key}` : key
      into[apiKey] = [...(into[apiKey] ?? []), values]
    }
    if (Object.keys(entries).length || Object.keys(notEntries).length) {
      targets.push({
        ...(Object.keys(entries).length ? { entries } : {}),
        ...(Object.keys(notEntries).length ? { notEntries } : {}),
      })
    }
  }
  return targets
}

function countKeyValue(targets?: KeyValueTarget[]): number {
  return parseKeyValueTargets(targets).reduce((n, group) => n + group.length, 0)
}

// ─── Segment rows ───────────────────────────────────────────────────────────
//
// The ad server reads the rows of `userSegmentTargets` as alternatives
// (NegatableTargetPayloadMatcher in core): a user matches a row when they are
// in every one of its `userSegments` and not in all of its `notUserSegments`
// at once, and matching any one row is enough. A row holding only
// `notUserSegments` is the exception: it turns away the users it names
// whatever the other rows say, and lets everyone else through — so beside such
// a row, the rows that include segments decide nothing.

const segmentsOf = (row: UserSegmentTarget) => row.userSegments ?? []
const notSegmentsOf = (row: UserSegmentTarget) => row.notUserSegments ?? []

/**
 * Reads segment rows back into one list of alternatives — a row each, one
 * segment per row, the way the old UI's simple mode saves them — or null when
 * the rows say more: an AND inside a row, or any NOT. A segment in two rows is
 * still one alternative, so it is listed once.
 */
export function readIncludedSegments(rows: UserSegmentTarget[] = []): ObjectRef[] | null {
  const used = rows.filter((row) => segmentsOf(row).length || notSegmentsOf(row).length)
  if (!used.every((row) => segmentsOf(row).length === 1 && !notSegmentsOf(row).length)) return null
  const segments = used.map((row) => segmentsOf(row)[0])
  return [...new Map(segments.map((segment) => [segment.id, segment])).values()]
}

/** The segment rows for a list of alternatives: the user must be in one of them. */
export function writeIncludedSegments(segments: ObjectRef[]): UserSegmentTarget[] {
  return segments.map((segment) => ({ userSegments: [segment] }))
}

/**
 * True when a row that only excludes sits beside a row that includes, which
 * leaves the including rows deciding nothing. This editor's earlier
 * include/exclude lists saved "in A, not in X" this way, and the old UI's
 * groups make it easy to, so line items can hold it.
 */
export function hasIgnoredSegmentRows(rows: UserSegmentTarget[] = []): boolean {
  return (
    rows.some((row) => !segmentsOf(row).length && notSegmentsOf(row).length > 0) &&
    rows.some((row) => segmentsOf(row).length > 0)
  )
}

// ─── Whole-object helpers ───────────────────────────────────────────────────

/** How many targeting types are in use. */
export function countTargetingTypes(targeting: Targeting | undefined): number {
  if (!targeting) return 0
  return TARGETING_TYPES.filter((def) => def.count(targeting) > 0).length
}

/**
 * The whole targeting object, exactly as the API represents it: every one of
 * the 26 targets present, empty where nothing is targeted.
 *
 * The server clears a line item's targeting and re-applies what the save
 * carries (StatefulResourceHelper.updateTargeting), so a target left out of the
 * payload is a target left behind — and one sent empty is one cleared. Sending
 * the full shape is therefore both how targeting is set and how it is cleared.
 *
 * Each target defaults to empty and then has whatever the line item holds
 * spread over it, so properties this editor does not model — a `matchingGroup`,
 * a target type added to the API later — survive a round trip untouched.
 */
export function targetingForSave(targeting: Targeting = {}): Targeting {
  return {
    deviceTargets: targeting.deviceTargets ?? [],
    adUnitTarget: { adUnits: [], negated: false, ...targeting.adUnitTarget },
    userSegmentTargets: targeting.userSegmentTargets ?? [],
    dateTarget: { dateRanges: [], timeZoneSetting: 'SYSTEM', ...targeting.dateTarget },
    geospatialTargets: targeting.geospatialTargets ?? [],
    keyValueTargets: targeting.keyValueTargets ?? [],
    siteTarget: { sites: [], negated: false, ...targeting.siteTarget },
    adUnitMatchingLabelTargets: targeting.adUnitMatchingLabelTargets ?? [],
    categoryTargets: targeting.categoryTargets ?? [],
    namedLocationTarget: {
      locations: [],
      municipalities: [],
      negated: false,
      ...targeting.namedLocationTarget,
    },
    dayPartingTargets: targeting.dayPartingTargets ?? [],
    retargetingTargets: targeting.retargetingTargets ?? [],
    keywordTargets: targeting.keywordTargets ?? [],
    similarKeywordTargets: targeting.similarKeywordTargets ?? [],
    ipAddressTarget: { addresses: [], ...targeting.ipAddressTarget },
    siteGroupTarget: { siteGroups: [], negated: false, ...targeting.siteGroupTarget },
    domainNameTarget: { names: [], negated: false, ...targeting.domainNameTarget },
    viewabilityTarget: { ...targeting.viewabilityTarget },
    semanticTargets: targeting.semanticTargets ?? [],
    firstPartyAudienceTarget: { firstPartyAudiences: [], ...targeting.firstPartyAudienceTarget },
    thirdPartyAudienceTargets: targeting.thirdPartyAudienceTargets ?? [],
    publisherTarget: { earningsAccounts: [], negated: false, ...targeting.publisherTarget },
    articleTarget: { urls: [], negated: false, ...targeting.articleTarget },
    siteCountryTarget: { countries: [], ...targeting.siteCountryTarget },
    weatherTargets: targeting.weatherTargets ?? [],
    organisationTarget: { organisations: [], negated: false, ...targeting.organisationTarget },
  }
}

// ─── Date ranges ────────────────────────────────────────────────────────────
//
// A date target carries local date-times without a zone (`YYYY-MM-DDTHH:mm`),
// interpreted in the zone `timeZoneSetting` names. The pickers work in ISO
// instants, so the two are converted at the edges — through UTC, exactly as the
// existing UI does, so a range saved in one reads back the same in the other.

export function toApiDateTime(iso: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 16)
}

export function fromApiDateTime(local: string): string {
  if (!local) return ''
  const date = new Date(`${local}Z`)
  return isNaN(date.getTime()) ? '' : date.toISOString()
}

/**
 * True when a keyword or category target says more than the three-input editor
 * can: further AND groups, or the `notKeywords`/`notCategories` lists. Those
 * rows are left exactly as they are, and the editor says so.
 */
export function hasAdvancedRows(
  rows: { keywords?: string[]; notKeywords?: string[] }[] | undefined
): boolean
export function hasAdvancedRows(
  rows: { categories?: string[]; notCategories?: string[] }[] | undefined
): boolean
export function hasAdvancedRows(rows: Record<string, string[] | undefined>[] | undefined): boolean {
  if (!rows?.length) return false
  const notLists = rows.flatMap((row) =>
    Object.entries(row)
      .filter(([key]) => key.startsWith('not'))
      .map(([, value]) => value ?? [])
  )
  return rows.length > 1 || notLists.some((list) => list.length > 0)
}

/** The editor's types for a list of backend `TargetType` names, e.g. a product's. */
export function typeIdsForApiTypes(apiTypes: string[] | undefined): TargetingTypeId[] {
  const wanted = new Set(apiTypes ?? [])
  return TARGETING_TYPES.filter((def) => wanted.has(def.apiType)).map((def) => def.id)
}

/** The types a targeting actually uses. */
export function typesInUse(targeting: Targeting | undefined): TargetingTypeId[] {
  if (!targeting) return []
  return TARGETING_TYPES.filter((def) => def.count(targeting) > 0).map((def) => def.id)
}
